import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';

const RollingRosterPrintView = ({ roster, startDate }) => {
  // Week and day constants
  const weeks = [1, 2, 3, 4, 5, 6];
  const days = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  // Calculate week dates for display
  const weekDates = useMemo(() => {
    if (!startDate || !roster) return [];
    
    const dates = [];
    const baseDate = new Date(startDate);
    
    // Adjust to Monday if not already Monday
    const dayOfWeek = baseDate.getDay();
    const daysToMonday = dayOfWeek === 0 ? 1 : (dayOfWeek === 1 ? 0 : 1 - dayOfWeek);
    const mondayDate = new Date(baseDate);
    mondayDate.setDate(mondayDate.getDate() + daysToMonday);
    
    for (let week = 0; week < 6; week++) {
      const weekStart = new Date(mondayDate);
      weekStart.setDate(weekStart.getDate() + (week * 7));
      
      const weekDatesArray = [];
      for (let day = 0; day < 7; day++) {
        const date = new Date(weekStart);
        date.setDate(date.getDate() + day);
        
        weekDatesArray.push({
          date: date.toISOString().split('T')[0],
          display: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
          dayIndex: date.getDay()
        });
      }
      
      dates.push({
        weekNumber: week + 1,
        startDate: weekStart.toISOString().split('T')[0],
        dates: weekDatesArray
      });
    }
    
    return dates;
  }, [startDate, roster]);

  // Helper to get day display for print
  const getDayDisplay = (weekNumber, dayKey) => {
    if (!roster) return 'Not set';
    
    const weekKey = `week${weekNumber}`;
    const dayData = roster[weekKey]?.[dayKey];
    
    if (!dayData || dayData === null) {
      return 'Not set';
    }
    
    const isUnavailable = dayData.isUnavailable || dayData.isSickLeave;
    
    if (isUnavailable) {
      return `UNAVAILABLE: ${dayData.unavailabilityReason || dayData.sicknessReason || 'No reason'}`;
    } else {
      return `AVAILABLE: ${dayData.startTime || '08:00'} - ${dayData.endTime || '16:00'}`;
    }
  };

  // Format period dates
  const formatPeriod = () => {
    if (!roster || !roster.startDate || !roster.endDate) {
      return 'Period not set';
    }
    
    try {
      const start = new Date(roster.startDate);
      const end = new Date(roster.endDate);
      
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return 'Invalid period dates';
      }
      
      return `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
    } catch (error) {
      return 'Error formatting period';
    }
  };

  // Get date for specific week and day
  const getDateForDay = (weekNumber, dayIndex) => {
    if (!weekDates[weekNumber - 1]) return '';
    
    const weekData = weekDates[weekNumber - 1];
    if (!weekData.dates[dayIndex]) return '';
    
    return weekData.dates[dayIndex].display;
  };

  return (
    <Box sx={{ p: 3 }} className="print-view">
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .print-view, .print-view * {
              visibility: visible;
            }
            .print-view {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              padding: 20px;
            }
            .no-print {
              display: none !important;
            }
            .print-table {
              page-break-inside: avoid;
            }
            @page {
              size: landscape;
              margin: 20mm;
            }
          }
          .print-view {
            background-color: white;
          }
        `}
      </style>
      
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4, borderBottom: '2px solid #000', pb: 2 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          6-Week Rolling Availability Roster
        </Typography>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ textAlign: 'left' }}>
            <Typography variant="h6" fontWeight="bold">
              {roster?.employeeName || 'Staff Member'}
            </Typography>
            <Typography variant="body1">
              {roster?.jobTitle || 'Job Title'} • {roster?.ward || 'Ward/Department'}
            </Typography>
            <Typography variant="body2">
              Email: {roster?.employeeEmail || 'N/A'}
            </Typography>
          </Box>
          
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body1" fontWeight="bold">
              Period: {formatPeriod()}
            </Typography>
            <Typography variant="body2">
              Generated: {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}
            </Typography>
            <Typography variant="caption">
              Beaumont Hospital
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Weekly Schedule Tables */}
      {weeks.map((week) => (
        <Box key={week} sx={{ mb: 4, pageBreakInside: 'avoid' }}>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 2, borderBottom: '1px solid #ddd', pb: 1 }}>
            Week {week} Schedule
          </Typography>
          
          <TableContainer component={Paper} sx={{ mb: 3, border: '1px solid #ddd' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell width="15%" sx={{ fontWeight: 'bold', borderRight: '1px solid #ddd' }}>
                    Day
                  </TableCell>
                  <TableCell width="20%" sx={{ fontWeight: 'bold', borderRight: '1px solid #ddd' }}>
                    Date
                  </TableCell>
                  <TableCell width="65%" sx={{ fontWeight: 'bold' }}>
                    Availability / Unavailability
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {days.map((day, index) => {
                  const dateDisplay = getDateForDay(week, index);
                  const availability = getDayDisplay(week, day.key);
                  const isUnavailable = availability.includes('UNAVAILABLE');
                  
                  return (
                    <TableRow 
                      key={day.key}
                      sx={{ 
                        backgroundColor: isUnavailable ? '#fff0f0' : '#f9f9f9',
                        '&:hover': { backgroundColor: isUnavailable ? '#ffe8e8' : '#f0f0f0' }
                      }}
                    >
                      <TableCell sx={{ borderRight: '1px solid #ddd', fontWeight: 'bold' }}>
                        {day.label}
                      </TableCell>
                      <TableCell sx={{ borderRight: '1px solid #ddd' }}>
                        {dateDisplay}
                      </TableCell>
                      <TableCell sx={{ 
                        color: isUnavailable ? '#d32f2f' : '#2e7d32',
                        fontWeight: isUnavailable ? 'bold' : 'normal'
                      }}>
                        {availability}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Notes Section for the Week */}
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f0f8ff', border: '1px solid #b3e0ff', borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              Week {week} Notes:
            </Typography>
            <Typography variant="body2">
              {(() => {
                const weekKey = `week${week}`;
                const weekData = roster?.[weekKey];
                if (!weekData) return 'No specific notes for this week.';
                
                // Collect any comments from days
                const comments = [];
                days.forEach(day => {
                  const dayData = weekData[day.key];
                  if (dayData?.comments) {
                    comments.push(`${day.label}: ${dayData.comments}`);
                  }
                });
                
                return comments.length > 0 
                  ? comments.join(' | ') 
                  : 'No specific notes for this week.';
              })()}
            </Typography>
          </Box>
        </Box>
      ))}

      {/* Summary Section */}
      <Box sx={{ mt: 4, p: 3, backgroundColor: '#f8f8f8', border: '2px solid #ddd', borderRadius: 2 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Roster Summary
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Total Available Days:</strong> {
                weeks.reduce((total, week) => {
                  const weekKey = `week${week}`;
                  const weekData = roster?.[weekKey];
                  if (!weekData) return total;
                  
                  const availableDays = days.filter(day => {
                    const dayData = weekData[day.key];
                    return dayData && !dayData.isUnavailable && !dayData.isSickLeave;
                  }).length;
                  
                  return total + availableDays;
                }, 0)
              } / {weeks.length * days.length}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2">
              <strong>Total Unavailable Days:</strong> {
                weeks.reduce((total, week) => {
                  const weekKey = `week${week}`;
                  const weekData = roster?.[weekKey];
                  if (!weekData) return total;
                  
                  const unavailableDays = days.filter(day => {
                    const dayData = weekData[day.key];
                    return dayData && (dayData.isUnavailable || dayData.isSickLeave);
                  }).length;
                  
                  return total + unavailableDays;
                }, 0)
              } / {weeks.length * days.length}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" sx={{ mt: 1 }}>
              <strong>Notes:</strong> This roster is a template for generating future availability schedules. 
              Changes made here will affect all future periods generated from this template.
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Footer */}
      <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #000', textAlign: 'center' }}>
        <Typography variant="caption">
          Beaumont Hospital - Staff Availability Management System
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
          Printed on: {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}
        </Typography>
      </Box>
    </Box>
  );
};

export default RollingRosterPrintView;